import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { AsyncActivityState } from '@/components/ui/async-activity-strip'
import { mergeGdocSyncFrontmatter } from '@/lib/gdoc-sync-frontmatter'
import {
  registerGoogleDocWatch,
  resolveGoogleDocSync,
  stopGoogleDocWatch,
  syncGoogleDoc,
  updateGdocSyncModeFrontmatter,
} from '@/lib/google-drive-sync-api'

const POLL_MS = 45_000
const SUCCESS_HOLD_MS = 2_000
/** After consecutive silent failures, pause polling (focus still allowed after backoff). */
const SILENT_FAIL_BACKOFF_MS = [30_000, 60_000, 120_000, 300_000] as const

type UseGdocSyncArgs = {
  gdocId: string | null | undefined
  documentId: string | null | undefined
  organizationId?: string | null
  frontmatter: Record<string, unknown> | null | undefined
  setFrontmatter: (fm: Record<string, unknown> | null) => void
  refreshDocument: () => Promise<void> | void
  /** Bumps when a successful local save completes - triggers push-side Sync when keep_synced */
  saveGeneration?: number
}

type RunSyncOpts = {
  force?: 'pull' | 'push'
  silent?: boolean
  /** Menu Sync / enabling Keep synced: show chooser when bodies differ. */
  interactive?: boolean
}

export function useGdocSync({
  gdocId,
  documentId,
  organizationId,
  frontmatter,
  setFrontmatter,
  refreshDocument,
  saveGeneration = 0,
}: UseGdocSyncArgs) {
  const [activity, setActivity] = useState<AsyncActivityState>('idle')
  const [activityLabel, setActivityLabel] = useState<string | null>(null)
  const [diffOpen, setDiffOpen] = useState(false)
  const [localMarkdown, setLocalMarkdown] = useState('')
  const [driveMarkdown, setDriveMarkdown] = useState('')
  const [localModifiedTime, setLocalModifiedTime] = useState<string | null>(null)
  const [driveModifiedTime, setDriveModifiedTime] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const syncingRef = useRef(false)
  const lastSaveGenRef = useRef(saveGeneration)
  const activatedForDocRef = useRef<string | null>(null)
  const frontmatterRef = useRef(frontmatter)
  frontmatterRef.current = frontmatter
  const silentFailCountRef = useRef(0)
  const silentBackoffUntilRef = useRef(0)

  const keepSynced = frontmatter?.gdoc_sync_mode === 'keep_synced'
  const hasConflict = frontmatter?.gdoc_sync_conflict === true
  const linked = Boolean(gdocId && documentId)

  const flashSuccess = useCallback((label: string) => {
    setActivity('success')
    setActivityLabel(label)
    window.setTimeout(() => {
      setActivity('idle')
      setActivityLabel(null)
    }, SUCCESS_HOLD_MS)
  }, [])

  const openSourceOfTruthChooser = useCallback(
    (result: {
      localMarkdown?: string
      driveMarkdown?: string
      localModifiedTime?: string | null
      driveModifiedTime?: string | null
    }) => {
      setLocalMarkdown(result.localMarkdown || '')
      setDriveMarkdown(result.driveMarkdown || '')
      setLocalModifiedTime(result.localModifiedTime ?? null)
      setDriveModifiedTime(result.driveModifiedTime ?? null)
      setDiffOpen(true)
      setActivity('conflict')
      setActivityLabel('Choose source of truth')
    },
    [],
  )

  const runSync = useCallback(
    async (opts?: RunSyncOpts) => {
      if (!documentId || !gdocId || syncingRef.current) return
      if (
        opts?.silent &&
        silentBackoffUntilRef.current > Date.now()
      ) {
        return
      }
      syncingRef.current = true
      if (!opts?.silent) {
        setActivity('working')
        setActivityLabel('Syncing with Drive…')
      }
      try {
        const result = await syncGoogleDoc({
          documentId,
          organizationId,
          force: opts?.force,
          interactive: opts?.interactive,
        })

        silentFailCountRef.current = 0
        silentBackoffUntilRef.current = 0

        if (result.frontmatter) {
          setFrontmatter(
            mergeGdocSyncFrontmatter(
              frontmatterRef.current,
              result.frontmatter as Record<string, unknown>,
            ),
          )
        }

        if (result.status === 'in_sync') {
          flashSuccess('Synced')
          return result
        }

        if (result.status === 'pull') {
          await refreshDocument()
          if (result.cosmeticOnly) {
            toast.message('Formatting-only - synced', {
              description: 'List markers / line breaks differed; pulled Drive export. No text conflict.',
            })
            flashSuccess('Synced - formatting only')
          } else {
            flashSuccess('Synced - pulled from Drive')
          }
          return result
        }

        if (result.status === 'push') {
          // Push re-exports Drive markdown and may rewrite soft breaks into content.
          await refreshDocument()
          if (result.cosmeticOnly) {
            toast.message('Formatting-only - synced', {
              description: 'Formatting differed; pushed Maater. No text conflict.',
            })
            flashSuccess('Synced - formatting only')
          } else {
            flashSuccess('Synced - pushed to Drive')
          }
          return result
        }

        // diverged (or interactive review when bodies differ)
        openSourceOfTruthChooser(result)
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setActivity('error')
        setActivityLabel("Couldn't sync")
        if (opts?.silent) {
          const n = Math.min(
            silentFailCountRef.current + 1,
            SILENT_FAIL_BACKOFF_MS.length,
          )
          silentFailCountRef.current = n
          const wait = SILENT_FAIL_BACKOFF_MS[n - 1] ?? SILENT_FAIL_BACKOFF_MS.at(-1)!
          silentBackoffUntilRef.current = Date.now() + wait
        } else {
          toast.error('Drive sync failed', { description: message })
        }
        return null
      } finally {
        syncingRef.current = false
      }
    },
    [
      documentId,
      gdocId,
      organizationId,
      setFrontmatter,
      refreshDocument,
      flashSuccess,
      openSourceOfTruthChooser,
    ],
  )

  const toggleKeepSynced = useCallback(async () => {
    if (!documentId || !gdocId) return
    const nextMode = keepSynced ? 'manual' : 'keep_synced'
    try {
      const nextFm = await updateGdocSyncModeFrontmatter({
        documentId,
        mode: nextMode,
        currentFrontmatter: frontmatter,
      })
      setFrontmatter(nextFm)
      if (nextMode === 'keep_synced') {
        const watch = await registerGoogleDocWatch({
          documentId,
          fileId: gdocId,
          organizationId,
        })
        toast.success('Keep synced on', {
          description: watch.watchRegistered
            ? 'Maater will sync while this doc is open and listen for Drive changes.'
            : 'Maater will sync while this doc is open (poll). Drive push notifications need a public HTTPS API URL.',
        })
        // Review first when sides differ - do not silent-push on enable.
        void runSync({ interactive: true })
      } else {
        await stopGoogleDocWatch(documentId)
        toast.success('Keep synced off')
      }
    } catch (err) {
      toast.error('Failed to update Keep synced', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }, [
    documentId,
    gdocId,
    keepSynced,
    frontmatter,
    setFrontmatter,
    organizationId,
    runSync,
  ])

  const acceptDrive = useCallback(async () => {
    if (!documentId) return
    setResolving(true)
    try {
      const result = await resolveGoogleDocSync({
        documentId,
        organizationId,
        resolvedMarkdown: driveMarkdown,
        alsoPush: false,
      })
      if (result.frontmatter) {
        setFrontmatter(
          mergeGdocSyncFrontmatter(frontmatter, result.frontmatter as Record<string, unknown>),
        )
      }
      setDiffOpen(false)
      await refreshDocument()
      flashSuccess('Synced - used Google Drive')
    } catch (err) {
      toast.error('Failed to accept Drive version', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setResolving(false)
    }
  }, [documentId, organizationId, driveMarkdown, frontmatter, setFrontmatter, refreshDocument, flashSuccess])

  const keepMaater = useCallback(async () => {
    if (!documentId) return
    setResolving(true)
    try {
      const result = await resolveGoogleDocSync({
        documentId,
        organizationId,
        resolvedMarkdown: localMarkdown,
        alsoPush: true,
      })
      if (result.frontmatter) {
        setFrontmatter(
          mergeGdocSyncFrontmatter(frontmatter, result.frontmatter as Record<string, unknown>),
        )
      }
      setDiffOpen(false)
      if (result.pushFailed) {
        toast.warning('Kept Maater locally', {
          description: `Drive push failed: ${result.pushFailed}. Try Sync now from the menu.`,
        })
        flashSuccess('Kept Maater (Drive push pending)')
      } else {
        flashSuccess('Synced - kept Maater')
      }
    } catch (err) {
      toast.error('Failed to keep Maater version', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setResolving(false)
    }
  }, [documentId, organizationId, localMarkdown, frontmatter, setFrontmatter, flashSuccess])

  /** Close chooser without picking a side. Keep synced stays on; conflict flag remains. */
  const cancelSourceOfTruth = useCallback(async () => {
    setDiffOpen(false)
    if (!documentId) return
    setActivity('conflict')
    setActivityLabel('Choose source of truth')
    if (!frontmatter) return

    const conflictFm = mergeGdocSyncFrontmatter(frontmatter, {
      gdoc_sync_conflict: true,
      ...(keepSynced ? { gdoc_sync_mode: 'keep_synced' } : {}),
    })
    setFrontmatter(conflictFm)

    try {
      const { updateDocument } = await import('@/lib/api/documents')
      await updateDocument(documentId, { frontmatter: conflictFm })
      if (keepSynced) {
        toast.message('Conflict needs resolution', {
          description:
            'Keep synced stays on. Choose Maater or Drive from Sync when you are ready.',
        })
      }
    } catch (err) {
      toast.error('Could not save conflict state', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }, [documentId, keepSynced, frontmatter, setFrontmatter])

  // Reset poll backoff when switching docs or leaving Keep synced
  useEffect(() => {
    silentFailCountRef.current = 0
    silentBackoffUntilRef.current = 0
    if (!keepSynced || !documentId) {
      activatedForDocRef.current = null
    }
  }, [documentId, gdocId, keepSynced])

  // Keep synced: Sync once when the linked doc tab becomes active; renew Drive watch
  useEffect(() => {
    if (!linked || !documentId || !gdocId || !keepSynced) {
      if (!documentId) activatedForDocRef.current = null
      return
    }
    if (activatedForDocRef.current === documentId) return
    activatedForDocRef.current = documentId
    void registerGoogleDocWatch({ documentId, fileId: gdocId, organizationId })
    void runSync({ silent: false })
  }, [linked, documentId, gdocId, keepSynced, organizationId, runSync])

  // While-open poll when Keep synced (auto one-sided; only opens chooser on true diverge)
  useEffect(() => {
    if (!linked || !keepSynced || !documentId) return
    const tick = () => {
      if (document.visibilityState === 'hidden') return
      void runSync({ silent: true })
    }
    const id = window.setInterval(tick, POLL_MS)
    // Focus: silent sync, but respect failure backoff (avoids console spam on flap)
    const onFocus = () => void runSync({ silent: true })
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [linked, keepSynced, documentId, runSync])

  // After local save: Sync (push when only-local) if Keep synced
  useEffect(() => {
    if (!linked || !keepSynced) return
    if (saveGeneration === lastSaveGenRef.current) return
    lastSaveGenRef.current = saveGeneration
    if (saveGeneration === 0) return
    void runSync({ silent: false })
  }, [saveGeneration, linked, keepSynced, runSync])

  // Surface conflict from frontmatter (e.g. closed-tab webhook)
  useEffect(() => {
    if (hasConflict && activity === 'idle') {
      setActivity('conflict')
      setActivityLabel('Choose source of truth')
    }
  }, [hasConflict, activity, keepSynced])

  return {
    linked,
    keepSynced,
    hasConflict,
    activity,
    activityLabel,
    runSync,
    toggleKeepSynced,
    diffOpen,
    setDiffOpen,
    localMarkdown,
    driveMarkdown,
    localModifiedTime,
    driveModifiedTime,
    acceptDrive,
    keepMaater,
    cancelSourceOfTruth,
    resolving,
  }
}
